import { useSyncExternalStore } from "react";
import { pollAudienceCount } from "./audienceCountPolling";
import {
  CountCommunicationAudienceMembers,
} from "../../api/communicationService";
import { Update } from "../../api/recordService";
import { PageRecord } from "../recordDefinition.types";
import { audienceEntityName, audienceRulesOf } from "./communicationAudienceRecord";

/** What a count in this session has found, ahead of the page being read again. */
export interface AudienceCountState {
  counting: boolean;
  count: number | null;
  countedOn: string | null;
}

const states = new Map<string, AudienceCountState>();
const listeners = new Set<() => void>();

function setState(id: string, state: AudienceCountState) {
  states.set(id, state);
  listeners.forEach((listener) => listener());
}

export function useAudienceCountState(id: string | undefined) {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);

      return () => listeners.delete(listener);
    },
    () => (id ? states.get(id) : undefined)
  );
}

// Zone-less UTC, which the service reads as the time it is.
export const utcNow = () => new Date().toISOString().slice(0, 19);

/**
 * How many people the record's rules find: null when there are no rules, which the service
 * refuses to count, or when the count failed or timed out.
 */
export async function countRules(
  dispatch: unknown,
  record: PageRecord
): Promise<number | null> {
  const rules = audienceRulesOf(record);

  if (rules.length === 0) {
    return null;
  }

  try {
    const response = await CountCommunicationAudienceMembers(dispatch, {
      EntityName: record.g4c_entitytype,
      Rules: rules,
      Type: Number(record.g4c_communicationaudiencetype),
    });

    return response?.data ? await pollAudienceCount(dispatch, response.data) : null;
  } catch {
    return null;
  }
}

/**
 * Counts a saved audience again and saves the result, for the Recount button. Only ever
 * given a saved audience's own rules, so the count is always of what is saved.
 */
export async function countAudience(
  dispatch: unknown,
  record: PageRecord
): Promise<number | null> {
  const id = String(record.id ?? "");

  setState(id, { counting: true, count: null, countedOn: null });

  const count = await countRules(dispatch, record);
  const countedOn = count === null ? null : utcNow();

  if (count !== null) {
    await Update(
      dispatch,
      "",
      {
        Id: record.id,
        Name: record.g4c_name,
        Attributes: { g4c_lastcount: count, g4c_lastcountedon: countedOn },
      },
      audienceEntityName
    );
  }

  setState(id, { counting: false, count, countedOn });

  return count;
}

/** What a count depends on, so one is known to be of the rules and type now on the page. */
export const rulesSignature = (record: PageRecord) =>
  JSON.stringify([
    audienceRulesOf(record),
    String(record.g4c_communicationaudiencetype),
  ]);

/**
 * Counts the rules about to be saved, so the count goes in with them and is always of
 * exactly what is stored. A count already taken of these very rules on the page - the
 * user pressed Recount before saving - is used rather than taken again. Left alone when
 * the rules and type are as they were, and for a new audience with no rules, which has
 * nothing to count. A count that fails is saved as none, rather than leaving the old one
 * beside rules it no longer describes.
 */
export async function countBeforeSave(
  record: PageRecord,
  original: PageRecord | null,
  dispatch: unknown
): Promise<PageRecord> {
  if (
    record.audience_counted_for &&
    record.audience_counted_for === rulesSignature(record)
  ) {
    return { ...record, audience_count_taken: true };
  }

  const unchanged = original
    ? rulesSignature(record) === rulesSignature(original)
    : audienceRulesOf(record).length === 0;

  if (unchanged) {
    return record;
  }

  const count = await countRules(dispatch, record);

  return {
    ...record,
    audience_count_taken: true,
    g4c_lastcount: count,
    g4c_lastcountedon: count === null ? null : utcNow(),
  };
}
